import { useQuery } from "@apollo/client";
import React, { useEffect, useMemo, useState } from "react";
import Spinner from "@src/components/spinner";
import { FileText, MapPin, ChevronRight, Clock, Flag, Settings, Building2, ArrowLeft, Check } from "lucide-react";
import { faArrowLeftLong, faCheck, faCircle, faClose, faFire } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import MapComponent from "@src/components/mapComponent/mapComponent";
import { Checkbox, Empty, Input, Modal, Typography, Select, Switch, Tag, Tooltip } from "antd";
import { getAllTaskInLibrary } from '@src/util/query/taskInLibrary.query';
import { BranchesOutlined, ClockCircleOutlined, FlagOutlined, EnvironmentOutlined, CaretDownFilled, InfoCircleOutlined } from "@ant-design/icons";
import Swal from 'sweetalert2';
import debounce from "lodash.debounce";
import { getSitesQuery } from "@src/util/query/site.query";
import { getSiteGroupLocationBySiteGroupIdQuery } from "@src/util/query/sitegrouplocation.query";
import { LocationSelector } from "@src/components/LocationSelector/LocationSelector.component";
import { getSiteCheckpointsQuery } from "@src/util/query/siteCheckpoint.query";


export const TaskAssignmentModal = ({
    visible, close,
    selectedTasks, setSelectedTasks,
    selectedCheckpoints = [], setSelectedCheckpoints = () => {},
    standAlone = false, handelSubmit = () => {}, loading = false,
    sitegroupId, associatedSiteGroupLocationId,
    siteId,
}) => {


    const { loading: taskListLoading, data: taskListData } = useQuery(getAllTaskInLibrary, {
        variables: { input: { groupId: null, isArchived: false } },
        fetchPolicy: 'network-only'
    });

    const { data: siteGroupLocationData, loading: siteGroupLocationLoading } = useQuery(getSiteGroupLocationBySiteGroupIdQuery, {
        variables: { siteGroupId: sitegroupId },
        fetchPolicy: 'network-only'
    });

    const { data: checkpointData, loading: checkpointLoading } = useQuery(getSiteCheckpointsQuery, {
        variables: { siteId },
        skip: !siteId,
        fetchPolicy: 'network-only'
    });

    const [sitegroupLocations, setSiteGroupLocations] = useState([]);
    useEffect(() => {
        if (siteGroupLocationData?.getSiteGroupLocationBySiteGroupId?.response && !siteGroupLocationLoading) {
            setSiteGroupLocations(siteGroupLocationData.getSiteGroupLocationBySiteGroupId.response);
        }
    }, [siteGroupLocationData?.getSiteGroupLocationBySiteGroupId?.response, siteGroupLocationLoading]);

    const [taskList, setTaskList] = useState([]);
    useEffect(() => {
        if (taskListData?.getAllTaskInLibrary?.response && !taskListLoading) {
            setTaskList(taskListData.getAllTaskInLibrary.response);
        }
    }, [taskListData?.getAllTaskInLibrary?.response, taskListLoading]);

    const checkpointList = checkpointData?.getSiteCheckpoints?.response || [];

    // ── Active item tracking ──────────────────────────────────────────────────────
    // activeItemType: 'task' | 'checkpoint' | null
    const [activeTaskId, setActiveTaskId] = useState(null);
    const [activeCheckpointId, setActiveCheckpointId] = useState(null);
    const [activeItemType, setActiveItemType] = useState(null);

    const [location, setLocation] = useState({ display: "", lat: "", lng: "" });
    const [configStep, setConfigStep] = useState('select');
    const [checkpointFrequency, setCheckpointFrequency] = useState({ type: null, value: null });

    const { Text } = Typography;

    // Normalised active item — same shape regardless of type
    const activeTask = selectedTasks.find(t => t?.taskId === activeTaskId);
    const activeCheckpointEntry = selectedCheckpoints.find(c => c?.checkpointId === activeCheckpointId);
    const activeItem = activeItemType === 'checkpoint' ? activeCheckpointEntry : activeTask;

    // Update whichever item type is currently active
    const updateActiveItemDetails = (updates) => {
        if (activeItemType === 'checkpoint') {
            setSelectedCheckpoints(prev => prev.map(c =>
                c?.checkpointId === activeCheckpointId ? { ...c, ...updates } : c
            ));
        } else {
            setSelectedTasks(selectedTasks.map(t =>
                t?.taskId === activeTaskId ? { ...t, ...updates } : t
            ));
        }
    };

    // Keep legacy helper for non-active task updates
    const updateTaskDetails = (taskId, updates) => {
        setSelectedTasks(selectedTasks.map(t =>
            t?.taskId === taskId ? { ...t, ...updates } : t
        ));
    };

    const clearActiveItem = () => {
        setActiveTaskId(null);
        setActiveCheckpointId(null);
        setActiveItemType(null);
        setConfigStep('select');
    };

    useEffect(() => {
        if (configStep === "site") {
            Swal.fire({
                icon: 'info',
                title: 'Automated Shift Creation',
                html: `
                    <div style="text-align: left; padding: 10px;">
                        <p style="margin-bottom: 10px;">This will create an <strong>automated shift</strong> on the selected site based on the task completion actual time.</p>
                        <p style="margin-bottom: 10px;"><strong>Benefits:</strong></p>
                        <ul style="margin-left: 20px; margin-bottom: 10px;">
                            <li>Accurate pay calculation</li>
                            <li>Transparent billing</li>
                            <li>Automatic record keeping</li>
                            <li>Real-time tracking</li>
                        </ul>
                    </div>
                `,
                confirmButtonText: 'Got it!',
                confirmButtonColor: '#1890ff',
                width: 500
            });
        }
    }, [configStep]);

    // ── Task selection ────────────────────────────────────────────────────────────
    const handleTaskSelection = (taskId, label, checked) => {
        if (checked) {
            setSelectedTasks([...selectedTasks, {
                taskId, label,
                assignmentType: null, priority: null,
                associatedSiteId: null, from: null, to: null, location: null,
                associatedSiteGroupLocationId: selectedLocation ? selectedLocation?._id : null
            }]);
            setActiveTaskId(taskId);
            setActiveCheckpointId(null);
            setActiveItemType('task');
            setConfigStep('action');
        } else {
            setSelectedTasks(selectedTasks.filter(t => t?.taskId !== taskId));
            if (activeTaskId === taskId) clearActiveItem();
        }
    };

    const handleLocationUpdate = () => {
        const locationData = sessionStorage.getItem("location");
        if (locationData) {
            updateActiveItemDetails({ location: JSON.parse(locationData) });
            sessionStorage.removeItem("location");
        }
    };

    // Checkpoints whose physical NFC tag hasn't been configured yet (scanned/
    // written from the mobile app) can't actually be patrolled - block assigning
    // them to a duty rather than let it fail silently for the guard on shift.
    // Any system user has the mobile-app privilege to configure a checkpoint for
    // any site, so the message surfaced from this points them there instead of
    // just refusing.
    const unconfiguredNfcCheckpoints = selectedCheckpoints
        .map(sc => checkpointList.find(c => c._id === sc.checkpointId))
        .filter(cp => cp?.tagType === 'nfc' && !cp?.nfcTagId);

    // ── Submit disabled check ─────────────────────────────────────────────────────
    const isSubmitDisabled = () => {
        if (selectedTasks.length === 0 && selectedCheckpoints.length === 0) return true;
        for (const task of selectedTasks) {
            if (!task.assignmentType) return true;
            if (task.assignmentType === 'priority' && task.priority === null) return true;
            if (task.assignmentType === 'schedule') {
                if (!task.from || !task.to) return true;
            }
        }
        if (selectedCheckpoints.length > 0 && !checkpointFrequency.type) return true;
        if (selectedCheckpoints.length > 0 && unconfiguredNfcCheckpoints.length > 0) return true;
        return false;
    };

    const getItemSummary = (item, isCheckpoint = false) => {
        if (isCheckpoint) {
            if (!checkpointFrequency.type) return null;
            const label = checkpointFrequency.type === 'interval'
                ? `Every ${checkpointFrequency.value}h`
                : `${checkpointFrequency.value}× per duty`;
            return (
                <div style={{ marginTop: '8px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    <Tag color="cyan" style={{ fontSize: '10px' }}>
                        <ClockCircleOutlined style={{ fontSize: '10px' }} /> {label}
                    </Tag>
                </div>
            );
        }
        const tags = [];
        if (item.assignmentType === 'priority' && item.priority !== null) {
            tags.push(
                <Tag key="priority" color="purple" style={{ fontSize: '10px' }}>
                    <FlagOutlined style={{ fontSize: '10px' }} /> Priority
                </Tag>
            );
        }
        if (item.assignmentType === 'schedule') {
            if (item.from && item.to) {
                tags.push(
                    <Tag key="time" color="orange" style={{ fontSize: '10px' }}>
                        <ClockCircleOutlined style={{ fontSize: '10px' }} /> Schedule
                    </Tag>
                );
            }
            if (item.location?.display) {
                tags.push(
                    <Tag key="location" color="green" style={{ fontSize: '10px' }}>
                        <EnvironmentOutlined style={{ fontSize: '10px' }} /> Location
                    </Tag>
                );
            }
        }
        return tags.length > 0 ? (
            <div style={{ marginTop: '8px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>{tags}</div>
        ) : null;
    };

    // ── Checkpoint frequency config ───────────────────────────────────────────────
    const renderCheckpointFrequencyConfig = () => {
        const isSelected = (type, value) => checkpointFrequency.type === type && checkpointFrequency.value === value;

        const handleSelect = (type, value) => {
            setCheckpointFrequency({ type, value });
            setSelectedCheckpoints(prev => prev.map(c => ({ ...c, frequencyType: type, frequencyValue: value })));
        };

        const intervalOptions = [
            { label: 'Every 1 hour', value: 1 },
            { label: 'Every 2 hours', value: 2 },
            { label: 'Every 3 hours', value: 3 },
            { label: 'Every 4 hours', value: 4 },
        ];
        const countOptions = [
            { label: 'Twice', value: 2 },
            { label: 'Thrice', value: 3 },
            { label: '4 times', value: 4 },
        ];

        const customIntervalValue = checkpointFrequency.type === 'interval' && ![1, 2, 3, 4].includes(checkpointFrequency.value) ? checkpointFrequency.value : '';
        const customCountValue = checkpointFrequency.type === 'count' && ![2, 3, 4].includes(checkpointFrequency.value) ? checkpointFrequency.value : '';

        return (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <div style={{ padding: '16px 24px', borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <Clock size={16} style={{ color: '#0091ae' }} />
                        <Text strong style={{ fontSize: '15px' }}>Set Checkpoint Frequency</Text>
                    </div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                        {selectedCheckpoints.length} checkpoint{selectedCheckpoints.length !== 1 ? 's' : ''} selected — all share the same frequency
                    </Text>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                    <div style={{ padding: '24px', maxWidth: '640px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '24px' }}>

                        {/* Interval */}
                        <div>
                            <div style={{ fontSize: '11px', fontWeight: '700', color: '#999', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '10px' }}>By Interval</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                {intervalOptions.map(opt => (
                                    <div
                                        key={opt.value}
                                        onClick={() => handleSelect('interval', opt.value)}
                                        style={{
                                            padding: '14px 16px', borderRadius: '12px', cursor: 'pointer',
                                            border: isSelected('interval', opt.value) ? '2px solid #0091ae' : '2px solid #e8e8e8',
                                            background: isSelected('interval', opt.value) ? 'rgba(0,145,174,0.06)' : 'white',
                                            display: 'flex', alignItems: 'center', gap: '10px', transition: 'all 0.2s'
                                        }}
                                    >
                                        <Clock size={16} style={{ color: isSelected('interval', opt.value) ? '#0091ae' : '#aaa', flexShrink: 0 }} />
                                        <span style={{ fontSize: '13px', fontWeight: '500', color: isSelected('interval', opt.value) ? '#0091ae' : '#333' }}>{opt.label}</span>
                                        {isSelected('interval', opt.value) && <Check size={14} style={{ color: '#0091ae', marginLeft: 'auto' }} />}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Count */}
                        <div>
                            <div style={{ fontSize: '11px', fontWeight: '700', color: '#999', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '10px' }}>By Count (per duty)</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                                {countOptions.map(opt => (
                                    <div
                                        key={opt.value}
                                        onClick={() => handleSelect('count', opt.value)}
                                        style={{
                                            padding: '16px 12px', borderRadius: '12px', cursor: 'pointer', textAlign: 'center',
                                            border: isSelected('count', opt.value) ? '2px solid #00bda5' : '2px solid #e8e8e8',
                                            background: isSelected('count', opt.value) ? 'rgba(0,189,165,0.06)' : 'white',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        <div style={{ fontSize: '22px', fontWeight: '700', color: isSelected('count', opt.value) ? '#00bda5' : '#333', lineHeight: 1 }}>{opt.value}×</div>
                                        <div style={{ fontSize: '11px', color: '#888', marginTop: '4px' }}>{opt.label}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Custom */}
                        <div>
                            <div style={{ fontSize: '11px', fontWeight: '700', color: '#999', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '10px' }}>Custom</div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                <div>
                                    <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: '6px' }}>Every  hours</Text>
                                    <Input
                                        type="number" min={1} placeholder="e.g. 5" size="large"
                                        value={customIntervalValue}
                                        onChange={(e) => { const v = parseInt(e.target.value); if (v > 0) handleSelect('interval', v); }}
                                        suffix="hrs"
                                        style={{ borderRadius: '8px', borderColor: customIntervalValue ? '#0091ae' : undefined }}
                                    />
                                </div>
                                <div>
                                    <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: '6px' }}>Per duty</Text>
                                    <Input
                                        type="number" min={1} placeholder="e.g. 5" size="large"
                                        value={customCountValue}
                                        onChange={(e) => { const v = parseInt(e.target.value); if (v > 0) handleSelect('count', v); }}
                                        suffix="×"
                                        style={{ borderRadius: '8px', borderColor: customCountValue ? '#00bda5' : undefined }}
                                    />
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        );
    };

    // ── Config screens (shared for tasks and checkpoints) ─────────────────────────

    const renderActionSelection = () => (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #f0f0f0' }}>
                <button
                    onClick={clearActiveItem}
                    style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#666', background: 'none', border: 'none', cursor: 'pointer', marginBottom: '12px', padding: '4px 0' }}
                >
                    <ArrowLeft size={16} /> Back to list
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <Settings size={16} style={{ color: '#1890ff' }} />
                    <Text strong style={{ fontSize: '15px' }}>
                        Configure {activeItemType === 'checkpoint' ? 'Checkpoint' : 'Task'}
                    </Text>
                </div>
                <Text type="secondary" style={{ fontSize: '12px' }}>{activeItem?.label}</Text>
            </div>

            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px' }}>
                <div style={{ width: '100%', maxWidth: '700px' }}>
                    <div style={{ textAlign: 'center', marginBottom: '32px' }}>
                        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px', color: '#333' }}>
                            Configure how this {activeItemType === 'checkpoint' ? 'checkpoint' : 'task'} should behave
                        </h3>
                        <p style={{ fontSize: '13px', color: '#999' }}>
                            Choose <strong>one main configuration</strong> below.
                        </p>
                    </div>

                    <div style={{ marginBottom: '24px' }}>
                        <div style={{ fontSize: '13px', color: '#555', fontWeight: '600', marginBottom: '12px' }}>Required – Choose one</div>
                        <div style={{ display: 'grid', gap: '16px' }}>

                            {/* Priority Card */}
                            <div
                                onClick={() => {
                                    updateActiveItemDetails({ assignmentType: 'priority', priority: activeItem?.priority ?? 0 });
                                    setConfigStep('priority');
                                }}
                                style={{
                                    padding: '20px', background: 'white', borderRadius: '16px',
                                    border: activeItem?.assignmentType === 'priority' ? '2px solid #667eea' : '2px solid #e8e8e8',
                                    cursor: 'pointer', transition: 'all 0.3s ease',
                                    boxShadow: activeItem?.assignmentType === 'priority' ? '0 8px 16px rgba(102,126,234,0.2)' : '0 2px 8px rgba(0,0,0,0.04)',
                                    transform: activeItem?.assignmentType === 'priority' ? 'translateY(-2px)' : 'none'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'start', gap: '16px' }}>
                                    <div style={{ width: '56px', height: '56px', borderRadius: '12px', background: '#667eea15', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <Flag size={24} style={{ color: '#667eea' }} />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                            <span style={{ fontWeight: '600', fontSize: '15px' }}>Set Priority</span>
                                            {activeItem?.assignmentType === 'priority' && (
                                                <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <Check size={12} style={{ color: 'white' }} />
                                                </div>
                                            )}
                                        </div>
                                        <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>Assign urgency level for completion</p>
                                    </div>
                                    <ChevronRight size={20} style={{ color: activeItem?.assignmentType === 'priority' ? '#667eea' : '#ccc', flexShrink: 0, marginTop: '4px' }} />
                                </div>
                            </div>

                            {/* Schedule Card */}
                            <div
                                onClick={() => {
                                    updateActiveItemDetails({ assignmentType: 'schedule' });
                                    setConfigStep('schedule');
                                }}
                                style={{
                                    padding: '20px', background: 'white', borderRadius: '16px',
                                    border: activeItem?.assignmentType === 'schedule' ? '2px solid #FF7A59' : '2px solid #e8e8e8',
                                    cursor: 'pointer', transition: 'all 0.3s ease',
                                    boxShadow: activeItem?.assignmentType === 'schedule' ? '0 8px 16px rgba(255,122,89,0.2)' : '0 2px 8px rgba(0,0,0,0.04)',
                                    transform: activeItem?.assignmentType === 'schedule' ? 'translateY(-2px)' : 'none'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'start', gap: '16px' }}>
                                    <div style={{ width: '56px', height: '56px', borderRadius: '12px', background: '#FF7A5915', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        <Clock size={24} style={{ color: '#FF7A59' }} />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                            <span style={{ fontWeight: '600', fontSize: '15px' }}>Set Schedule</span>
                                            {activeItem?.assignmentType === 'schedule' && (
                                                <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <Check size={12} style={{ color: 'white' }} />
                                                </div>
                                            )}
                                        </div>
                                        <p style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>Define time window and location</p>
                                    </div>
                                    <ChevronRight size={20} style={{ color: activeItem?.assignmentType === 'schedule' ? '#FF7A59' : '#ccc', flexShrink: 0, marginTop: '4px' }} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderPriorityConfig = () => (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #f0f0f0' }}>
                <button onClick={() => setConfigStep('action')} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#666', background: 'none', border: 'none', cursor: 'pointer', marginBottom: '12px', padding: '4px 0' }}>
                    <ArrowLeft size={16} /> Back to options
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <Flag size={16} style={{ color: '#667eea' }} />
                    <Text strong style={{ fontSize: '15px' }}>Set Priority Level</Text>
                </div>
                <Text type="secondary" style={{ fontSize: '12px' }}>{activeItem?.label}</Text>
            </div>

            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px' }}>
                <div style={{ width: '100%', maxWidth: '600px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {[
                        { value: 2, label: 'High Priority', color: '#ef4444', icon: <FontAwesomeIcon color="#ef4444" icon={faFire} />, desc: 'Urgent, needs immediate action' },
                        { value: 1, label: 'Medium Priority', color: '#f59e0b', icon: <FontAwesomeIcon color="#f59e0b" icon={faCircle} />, desc: 'Important but not urgent' },
                        { value: 0, label: 'Normal Priority', color: '#10b981', icon: <FontAwesomeIcon color="#10b981" icon={faCheck} />, desc: 'Standard routine task' }
                    ].map((priority) => (
                        <div
                            key={priority.value}
                            onClick={() => updateActiveItemDetails({ priority: priority.value })}
                            style={{
                                padding: '20px', borderRadius: '12px',
                                border: activeItem?.priority === priority.value ? `3px solid ${priority.color}` : '2px solid #e5e7eb',
                                background: activeItem?.priority === priority.value ? `${priority.color}08` : 'white',
                                cursor: 'pointer', transition: 'all 0.2s ease',
                                transform: activeItem?.priority === priority.value ? 'scale(1.02)' : 'scale(1)',
                                boxShadow: activeItem?.priority === priority.value ? `0 4px 12px ${priority.color}30` : 'none'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                <div style={{ width: '48px', height: '48px', borderRadius: '10px', background: `${priority.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', flexShrink: 0 }}>
                                    {priority.icon}
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontWeight: '600', fontSize: '15px', color: activeItem?.priority === priority.value ? priority.color : '#333', marginBottom: '4px' }}>{priority.label}</div>
                                    <div style={{ fontSize: '13px', color: '#999' }}>{priority.desc}</div>
                                </div>
                                {activeItem?.priority === priority.value && (
                                    <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: priority.color, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', flexShrink: 0 }}>
                                        <Check size={16} />
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                    <button
                        onClick={() => setConfigStep('action')}
                        style={{ marginTop: '16px', padding: '14px 24px', background: '#FF7A59', color: 'white', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}
                    >
                        Save Priority
                    </button>
                </div>
            </div>
        </div>
    );

    const [search, setSearch] = useState("");
    const { data: siteData, loading: siteLoading } = useQuery(getSitesQuery, {
        fetchPolicy: "cache-and-network",
        variables: { input: { filters: null, pageSize: 10, page: 1, search } }
    });
    const debounceSearch = useMemo(() => debounce((val) => setSearch(val), 500), []);

    const renderScheduleConfig = () => (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}>
                <button onClick={() => setConfigStep('action')} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#666', background: 'none', border: 'none', cursor: 'pointer', marginBottom: '12px', padding: '4px 0' }}>
                    <ArrowLeft size={16} /> Back to options
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <Clock size={16} style={{ color: '#FF7A59' }} />
                    <Text strong style={{ fontSize: '15px' }}>Set Schedule</Text>
                </div>
                <Text type="secondary" style={{ fontSize: '12px' }}>{activeItem?.label}</Text>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                <div style={{ padding: '32px', maxWidth: '800px', margin: '0 auto' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                        <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #f0f0f0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                                <Text strong style={{ fontSize: '14px' }}>
                                    <ClockCircleOutlined style={{ marginRight: '8px', color: '#f093fb' }} /> Time Window
                                </Text>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                                <div>
                                    <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: '8px', fontWeight: '500' }}>Start Time</Text>
                                    <Input type="time" size="large" value={activeItem?.from || ''} onChange={(e) => updateActiveItemDetails({ from: e.target.value })} status={(activeItem?.from && !activeItem?.to) ? 'error' : ''} style={{ borderRadius: '8px' }} />
                                </div>
                                <div>
                                    <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginBottom: '8px', fontWeight: '500' }}>End Time</Text>
                                    <Input type="time" size="large" value={activeItem?.to || ''} onChange={(e) => updateActiveItemDetails({ to: e.target.value })} status={(!activeItem?.from && activeItem?.to) ? 'error' : ''} style={{ borderRadius: '8px' }} />
                                </div>
                            </div>
                            {((activeItem?.from && !activeItem?.to) || (!activeItem?.from && activeItem?.to)) && (
                                <div style={{ marginTop: '12px', padding: '10px', background: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca', color: '#dc2626', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    ⚠️ Both start and end times are required
                                </div>
                            )}
                        </div>

                        <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #f0f0f0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                                <Text strong style={{ fontSize: '14px' }}>
                                    <EnvironmentOutlined style={{ marginRight: '8px', color: '#10b981' }} /> Location Pin
                                </Text>
                                <Tag color="default" style={{ fontSize: '10px' }}>Optional</Tag>
                            </div>
                            {activeItem?.location?.display && (
                                <div style={{ marginBottom: '16px', padding: '12px', background: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)', borderRadius: '8px', border: '1px solid #6ee7b7', fontSize: '12px', color: '#065f46', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <MapPin size={14} />
                                    <div>
                                        <strong style={{ display: 'block', fontSize: '11px' }}>Location Set</strong>
                                        <span style={{ fontSize: '11px', opacity: 0.9 }}>{activeItem.location.display}</span>
                                    </div>
                                </div>
                            )}
                            <div style={{ borderRadius: '12px', overflow: 'hidden', padding: '20px 30px' }}>
                                <MapComponent location={location} onLocationChange={handleLocationUpdate} />
                            </div>
                        </div>

                        <button
                            onClick={() => setConfigStep('action')}
                            style={{ padding: '14px 24px', background: '#FF7A59', color: 'white', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}
                        >
                            Save Schedule
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderSiteConfig = () => (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '16px 24px', borderBottom: '1px solid #f0f0f0' }}>
                <button onClick={() => setConfigStep('action')} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#666', background: 'none', border: 'none', cursor: 'pointer', marginBottom: '12px', padding: '4px 0' }}>
                    <ArrowLeft size={16} /> Back to options
                </button>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <Building2 size={16} style={{ color: '#8b5cf6' }} />
                    <Text strong style={{ fontSize: '15px' }}>Associate Site</Text>
                </div>
                <Text type="secondary" style={{ fontSize: '12px' }}>{activeItem?.label}</Text>
            </div>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '32px' }}>
                <div style={{ width: '100%', maxWidth: '600px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    <Select allowClear showSearch placeholder="Select a site" value={activeItem?.associatedSiteId} onChange={(val) => updateTaskDetails(activeTaskId, { associatedSiteId: val })} style={{ width: '100%' }} size="large" suffixIcon={<CaretDownFilled />} className="custom-select" onSearch={(val) => debounceSearch(val)} filterOption={false} loading={siteLoading}>
                        {siteData?.sites?.sites?.map((res) => (
                            <Select.Option key={res._id} value={res._id}>{res.sitename}</Select.Option>
                        ))}
                    </Select>
                    {activeItem?.associatedSiteId && (
                        <div style={{ padding: '16px', background: 'linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%)', borderRadius: '12px', border: '1px solid #bae6fd', fontSize: '12px', color: '#0369a1', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                            <InfoCircleOutlined style={{ fontSize: '16px', marginTop: '2px' }} />
                            <div>
                                <strong style={{ display: 'block', marginBottom: '6px', fontSize: '13px' }}>Automated Shift Enabled</strong>
                                <span style={{ fontSize: '12px', opacity: 0.9 }}>Shift will be auto-generated for pay & billing calculations on site schedule</span>
                            </div>
                        </div>
                    )}
                    <button onClick={() => setConfigStep('action')} style={{ marginTop: '16px', padding: '14px 24px', background: '#FF7A59', color: 'white', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
                        Save Site
                    </button>
                </div>
            </div>
        </div>
    );

    const [selectedLocation, setSelectedLocation] = useState(null);
    const isLocExist = sitegroupLocations?.length > 0 && !selectedLocation || false;

    useEffect(() => {
        if (associatedSiteGroupLocationId && sitegroupLocations?.length > 0) {
            setSelectedLocation(sitegroupLocations?.find((sgl) => sgl?._id == associatedSiteGroupLocationId));
        }
    }, [associatedSiteGroupLocationId, sitegroupLocations]);

    const totalSelected = selectedTasks.length + selectedCheckpoints.length;

    return (
        <Modal
            open={visible}
            width={isLocExist ? "700px" : "94%"}
            className={isLocExist ? '' : 'editDutyModalWrapper'}
            style={isLocExist ? { top: 0 } : { marginRight: '3.75em', top: 0 }}
            footer={
                isLocExist ? null :
                    <div className="drawer-footer" style={{ padding: '20px 24px', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ color: '#666', fontSize: '13px' }}>
                            {unconfiguredNfcCheckpoints.length > 0 ? (
                                <span style={{ color: '#ff4d4f' }}>
                                    {unconfiguredNfcCheckpoints.length} checkpoint{unconfiguredNfcCheckpoints.length > 1 ? 's' : ''} {unconfiguredNfcCheckpoints.length > 1 ? 'have' : 'has'} an NFC tag that hasn't been configured yet ({unconfiguredNfcCheckpoints.map(cp => cp.name).join(', ')}) — scan it from the mobile app to set it up (any user can configure a checkpoint for any site), then try again
                                </span>
                            ) : totalSelected > 0 && (
                                <>
                                    {selectedTasks.length > 0 && `${selectedTasks.length} task${selectedTasks.length > 1 ? 's' : ''}`}
                                    {selectedTasks.length > 0 && selectedCheckpoints.length > 0 && ', '}
                                    {selectedCheckpoints.length > 0 && `${selectedCheckpoints.length} checkpoint${selectedCheckpoints.length > 1 ? 's' : ''}`}
                                    {' selected'}
                                    {isSubmitDisabled() && [...selectedTasks, ...selectedCheckpoints].some(t => t.assignmentType === 'schedule' && ((t.from && !t.to) || (!t.from && t.to))) && (
                                        <span style={{ color: '#ff4d4f', marginLeft: '8px' }}>• Complete time range required</span>
                                    )}
                                </>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            {standAlone && totalSelected > 0 ? (
                                <button
                                    onClick={() => handelSubmit()}
                                    disabled={loading || isSubmitDisabled()}
                                    className={(loading || isSubmitDisabled()) ? 'disabled-btn drawer-filled-btn' : 'drawer-filled-btn'}
                                >
                                    {loading ? <Spinner /> : "Assign"}
                                </button>
                            ) : (
                                <button disabled={loading} className={loading ? 'disabled-btn drawer-outlined-btn' : 'drawer-outlined-btn'} onClick={close}>
                                    Close
                                </button>
                            )}
                        </div>
                    </div>
            }
            closable={false}
        >
            {sitegroupLocations?.length > 0 && !selectedLocation ?
                <LocationSelector locations={sitegroupLocations} onClose={close} onContinue={setSelectedLocation} />
                :
                <React.Fragment>
                    <div className='modal-header-title'>
                        {selectedLocation ?
                            <span onClick={() => setSelectedLocation(null)} style={{ cursor: 'pointer' }}>
                                <FontAwesomeIcon icon={faArrowLeftLong} />&emsp;
                                Assign Tasks & Checkpoints {`(on ${selectedLocation?.address})`}
                            </span>
                            :
                            <span>Assign Tasks &amp; Checkpoints</span>
                        }
                        <span onClick={() => { close(); }}><FontAwesomeIcon className='close' icon={faClose} /></span>
                    </div>

                    <div>
                        {(siteGroupLocationLoading || taskListLoading || checkpointLoading || loading) ? (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '500px' }}>
                                <Spinner />
                            </div>
                        ) : (
                            <div style={{ display: 'flex', height: '80vh', overflow: 'hidden' }}>

                                {/* ── LEFT PANEL ── */}
                                <div style={{ width: '380px', borderRight: '1px solid #f0f0f0', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>

                                    {/* Checkpoints compact toggle */}
                                    {siteId && (
                                        <div
                                            onClick={() => { if (selectedCheckpoints.length > 0) { setActiveItemType('checkpoint'); setConfigStep('checkpoint-frequency'); } }}
                                            style={{
                                                padding: '12px 16px', margin: '10px', borderRadius: '12px', flexShrink: 0,
                                                border: `2px solid ${selectedCheckpoints.length > 0 ? '#00bda5' : '#e8e8e8'}`,
                                                background: selectedCheckpoints.length > 0 ? 'rgba(0,189,165,0.05)' : '#fafafa',
                                                cursor: selectedCheckpoints.length > 0 ? 'pointer' : 'default',
                                                transition: 'all 0.2s',
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: selectedCheckpoints.length > 0 ? 'rgba(0,189,165,0.15)' : '#e8e8e8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                    <MapPin size={18} style={{ color: selectedCheckpoints.length > 0 ? '#00bda5' : '#aaa' }} />
                                                </div>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontWeight: '600', fontSize: '13px', color: selectedCheckpoints.length > 0 ? '#00bda5' : '#555' }}>
                                                        Checkpoints
                                                        {checkpointList.length > 0 && (
                                                            <span style={{ marginLeft: '6px', fontSize: '11px', fontWeight: 400, color: '#8c9bad' }}>
                                                                {selectedCheckpoints.length > 0 ? `${checkpointList.length} assigned` : `${checkpointList.length} available`}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {selectedCheckpoints.length > 0 && checkpointFrequency.type && (
                                                        <div style={{ fontSize: '11px', color: '#00bda5', marginTop: '2px' }}>
                                                            {checkpointFrequency.type === 'interval' ? `Every ${checkpointFrequency.value}h` : `${checkpointFrequency.value}× per duty`}
                                                        </div>
                                                    )}
                                                    {selectedCheckpoints.length > 0 && !checkpointFrequency.type && (
                                                        <div style={{ fontSize: '11px', color: '#f59e0b', marginTop: '2px' }}>Frequency not set</div>
                                                    )}
                                                    {selectedCheckpoints.length > 0 && unconfiguredNfcCheckpoints.length > 0 && (
                                                        <div style={{ fontSize: '11px', color: '#ff4d4f', marginTop: '2px' }}>{unconfiguredNfcCheckpoints.length} NFC tag{unconfiguredNfcCheckpoints.length > 1 ? 's' : ''} not configured</div>
                                                    )}
                                                </div>
                                                <Switch
                                                    size="small"
                                                    checked={selectedCheckpoints.length > 0}
                                                    disabled={checkpointList.length === 0}
                                                    onChange={(checked) => {
                                                        if (checked) {
                                                            const freq = checkpointFrequency.type ? { frequencyType: checkpointFrequency.type, frequencyValue: checkpointFrequency.value } : {};
                                                            setSelectedCheckpoints(checkpointList.map(cp => ({
                                                                checkpointId: cp._id, label: cp.name,
                                                                associatedSiteGroupLocationId: selectedLocation ? selectedLocation?._id : null,
                                                                ...freq
                                                            })));
                                                            setActiveItemType('checkpoint');
                                                            setConfigStep('checkpoint-frequency');

                                                            // Short heads-up right when checkpoints get turned on with an
                                                            // unconfigured NFC checkpoint included, instead of leaving it
                                                            // to the (longer) footer warning to be noticed.
                                                            const unconfigured = checkpointList.filter(cp => cp.tagType === 'nfc' && !cp.nfcTagId);
                                                            if (unconfigured.length > 0) {
                                                                Swal.fire({
                                                                    icon: 'warning',
                                                                    title: `${unconfigured.length} checkpoint${unconfigured.length > 1 ? 's' : ''} not configured`,
                                                                    text: `${unconfigured.map(cp => cp.name).join(', ')} ${unconfigured.length > 1 ? 'need' : 'needs'} an NFC tag scan from the mobile app before assigning.`,
                                                                    confirmButtonText: 'OK',
                                                                    customClass: { confirmButton: 'swal-theme-confirm-btn' }
                                                                });
                                                            }
                                                        } else {
                                                            setSelectedCheckpoints([]);
                                                            setCheckpointFrequency({ type: null, value: null });
                                                            if (activeItemType === 'checkpoint') clearActiveItem();
                                                        }
                                                    }}
                                                    onClick={(_, e) => e.stopPropagation()}
                                                    style={{ flexShrink: 0 }}
                                                />
                                                {selectedCheckpoints.length > 0 && (
                                                    <ChevronRight size={16} style={{ color: '#00bda5', flexShrink: 0 }} />
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Tasks section */}
                                    <div style={{ padding: '12px 20px 8px', backgroundColor: '#fafafa', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 1 }}>
                                        <Text strong style={{ fontSize: '13px' }}>Available Tasks</Text>
                                        {taskList?.length > 0 && (
                                            <Checkbox
                                                checked={selectedTasks.length === taskList.length}
                                                indeterminate={selectedTasks.length > 0 && selectedTasks.length < taskList.length}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedTasks(taskList.map(t => ({
                                                            taskId: t._id, label: t.label,
                                                            assignmentType: null, priority: null,
                                                            associatedSiteId: null, from: null, to: null, location: null,
                                                            associatedSiteGroupLocationId: selectedLocation ? selectedLocation?._id : null
                                                        })));
                                                    } else {
                                                        setSelectedTasks([]);
                                                        if (activeItemType === 'task') clearActiveItem();
                                                    }
                                                }}
                                            >
                                                <span style={{ fontSize: '12px' }}>Select all</span>
                                            </Checkbox>
                                        )}
                                    </div>

                                    <div style={{ padding: '8px' }}>
                                        {taskList?.length > 0 ? taskList.map((task) => {
                                            const selectedTask = selectedTasks.find(t => t.taskId === task._id);
                                            const isSelected = !!selectedTask;
                                            const isActive = activeTaskId === task._id && activeItemType === 'task';
                                            return (
                                                <div
                                                    key={task._id}
                                                    onClick={() => { if (isSelected) { setActiveTaskId(task._id); setActiveCheckpointId(null); setActiveItemType('task'); setConfigStep('action'); } }}
                                                    style={{
                                                        padding: '12px', marginBottom: '6px',
                                                        border: `2px solid ${isActive ? '#1890ff' : isSelected ? '#52c41a' : '#f0f0f0'}`,
                                                        borderRadius: '12px',
                                                        backgroundColor: isSelected ? (isActive ? '#e6f7ff' : '#f6ffed') : 'white',
                                                        cursor: 'pointer', transition: 'all 0.2s ease',
                                                        boxShadow: isActive ? '0 2px 8px rgba(24,144,255,0.15)' : 'none'
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                                        <Checkbox checked={isSelected} onChange={(e) => { e.stopPropagation(); handleTaskSelection(task._id, task.label, e.target.checked); }} style={{ marginTop: '2px' }} />
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div style={{ fontWeight: '500', marginBottom: '4px', fontSize: '13px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{task.label}</span>
                                                                {task?.flow && <Tooltip title="Form attached"><FileText size={14} style={{ color: '#1890ff', flexShrink: 0 }} /></Tooltip>}
                                                            </div>
                                                            <div style={{ fontSize: '11px', color: '#999', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                                <BranchesOutlined style={{ fontSize: '11px' }} />
                                                                {task.flow === "formBase" ? "Form template" : "Field base"}
                                                            </div>
                                                            {isSelected && getItemSummary(selectedTask)}
                                                        </div>
                                                        {isSelected && <ChevronRight size={16} style={{ color: isActive ? '#1890ff' : '#999', flexShrink: 0, marginTop: '2px' }} />}
                                                    </div>
                                                </div>
                                            );
                                        }) : (
                                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No available tasks" style={{ marginTop: '20px' }} />
                                        )}
                                    </div>

                                </div>

                                {/* ── RIGHT PANEL ── */}
                                {configStep === 'select' || (configStep !== 'checkpoint-frequency' && !activeItem) ? (
                                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999' }}>
                                        <div style={{ textAlign: 'center' }}>
                                            <Settings size={48} style={{ color: '#d9d9d9', marginBottom: '16px' }} />
                                            <Text type="secondary" style={{ fontSize: '14px', display: 'block' }}>Select a task or checkpoint to configure</Text>
                                            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>Choose an item from the list to get started</Text>
                                        </div>
                                    </div>
                                ) : configStep === 'checkpoint-frequency' ? renderCheckpointFrequencyConfig()
                                    : configStep === 'action' ? renderActionSelection()
                                        : configStep === 'priority' ? renderPriorityConfig()
                                            : configStep === 'schedule' ? renderScheduleConfig()
                                                : configStep === 'site' ? renderSiteConfig()
                                                    : null}
                            </div>
                        )}
                    </div>
                </React.Fragment>
            }
        </Modal>
    );
};
